import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { functions } from '@/lib/firebase';
import { httpsCallable } from 'firebase/functions';
import { CheckCircle2, Loader2, AlertCircle } from 'lucide-react';

export default function InitStats() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);

  const initializeStats = async () => {
    setLoading(true);
    setResult(null);

    try {
      if (!functions) {
        throw new Error('Firebase Functions not initialized');
      }

      const updateStats = httpsCallable(functions, 'updateStats');
      await updateStats();
      
      setResult({
        success: true,
        message: 'Stats initialized successfully! Check your home page.'
      });
    } catch (error: any) {
      console.error('Error initializing stats:', error);
      setResult({
        success: false,
        message: error.message || 'Failed to initialize stats. Make sure you are logged in and functions are deployed.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-20">
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle>Initialize Statistics</CardTitle>
          <CardDescription>
            This will calculate and initialize the public statistics (users count, invoices count, countries count) 
            by reading from your Firestore database and writing to the publicStats collection.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <h3 className="font-semibold">What this does:</h3>
            <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
              <li>Counts all users in your database</li>
              <li>Counts all invoices across all users</li>
              <li>Extracts unique countries from user data</li>
              <li>Creates/updates the publicStats/global document</li>
            </ul>
          </div>

          <Button 
            onClick={initializeStats} 
            disabled={loading}
            className="w-full"
            size="lg"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Initializing...
              </>
            ) : (
              'Initialize Stats Now'
            )}
          </Button>

          {result && (
            <div className={`p-4 rounded-lg flex items-start gap-3 ${
              result.success 
                ? 'bg-green-50 text-green-900 border border-green-200' 
                : 'bg-red-50 text-red-900 border border-red-200'
            }`}>
              {result.success ? (
                <CheckCircle2 className="h-5 w-5 mt-0.5 flex-shrink-0" />
              ) : (
                <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
              )}
              <div className="flex-1">
                <p className="font-medium">{result.success ? 'Success!' : 'Error'}</p>
                <p className="text-sm mt-1">{result.message}</p>
              </div>
            </div>
          )}

          <div className="text-sm text-muted-foreground space-y-2 pt-4 border-t">
            <p><strong>Note:</strong> After initialization, stats will automatically update:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Every hour via Cloud Scheduler</li>
              <li>When new users sign up</li>
              <li>When invoices are created or deleted</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
